import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Badge, Tooltip, Button, Tag, Empty, message, Spin } from 'antd';
import {
    CheckCircleFilled,
    CheckOutlined,
    LockOutlined,
    UnlockOutlined,
    SkinOutlined,
    StarOutlined,
    ThunderboltOutlined,
    AimOutlined,
    SafetyCertificateOutlined,
    UserOutlined,
    SaveOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { getJavamonImage, UnlockUserItem, LockUserItem, SetUserEquipped, GetUserEquipment } from '../../../dao/jutge/GetJavamonConfig';

const javamonImage = (item: any, fallbackType?: string): string => {
    if (!item) return '';
    if (item.imagen && item.imagen.startsWith('data:')) return item.imagen;
    const type = item._type || fallbackType || 'skin';
    if (item.id) {
        return `${process.env.REACT_APP_API_URL}:8061/jutge/game/image/${type}/${item.id}`;
    }
    return getJavamonImage({ ...item, _type: type });
};


interface UserEquipmentEditModalProps {
    visible: boolean;
    onClose: () => void;
    editingUser: any;
    skinsConfig: {
        skins: any[];
        armes: any[];
        escuts: any[];
        arcs: any[];
        flechas: any[];
        magias: any[];
        avatars: any[];
    };
    /** @deprecated Se usa onSaved para notificar al padre tras cada guardado automático */
    onSave?: (payload: any) => Promise<void>;
    /** Callback llamado tras cada guardado automático exitoso (para que el padre refresque datos) */
    onSaved?: () => void;
}

type EquipCategory = 'skin' | 'arma' | 'escut' | 'arc' | 'flecha' | 'magia' | 'avatar';

interface CategoryConfig {
    key: EquipCategory;
    title: string;
    icon: React.ReactNode;
    color: string;
    list: any[];
    equippedField: string;
    unlockedKey: string;
    hasEquippedState: boolean; // avatars only have unlocked, other 6 have equipped + unlocked
}

const UserEquipmentEditModal: React.FC<UserEquipmentEditModalProps> = ({
    visible,
    onClose,
    editingUser,
    skinsConfig,
    onSave,
    onSaved
}) => {
    // Estado inicial derivado de editingUser
    const [equipped, setEquipped] = useState<Record<string, number | null>>(() => ({
        id_skin: editingUser?.id_skin || null,
        id_arma: editingUser?.id_arma || null,
        id_escut: editingUser?.id_escut || null,
        id_arc: editingUser?.id_arc || null,
        id_flecha: editingUser?.id_flecha || null,
        id_magia: editingUser?.id_magia || null,
        id_avatar: editingUser?.id_avatar || null
    }));

    const parseUnlocked = (raw: any): number[] => {
        if (!raw) return [1];
        // Si la API devuelve ya un array (en vez de string CSV)
        if (Array.isArray(raw)) {
            const arr = raw.map((x: any) => parseInt(x, 10)).filter((n: number) => !isNaN(n));
            if (!arr.includes(1)) arr.push(1);
            return arr;
        }
        // String CSV "1,2,3"
        const arr = String(raw).split(',').map((x: string) => parseInt(x, 10)).filter((n: number) => !isNaN(n));
        if (!arr.includes(1)) arr.push(1);
        return arr;
    };

    const [unlockedState, setUnlockedState] = useState<Record<string, number[]>>(() => ({
        unlocked_avatars: parseUnlocked(editingUser?.unlocked_avatars),
        unlocked_skins: parseUnlocked(editingUser?.unlocked_skins),
        unlocked_armes: parseUnlocked(editingUser?.unlocked_armes),
        unlocked_escuts: parseUnlocked(editingUser?.unlocked_escuts),
        unlocked_arcs: parseUnlocked(editingUser?.unlocked_arcs),
        unlocked_flechas: parseUnlocked(editingUser?.unlocked_flechas),
        unlocked_magias: parseUnlocked(editingUser?.unlocked_magias)
    }));

    const [activeTab, setActiveTab] = useState<string>('skin');
    const [saving, setSaving] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);

    // Cargar siempre la información fresca de equipamiento y desbloqueos directamente del endpoint
    useEffect(() => {
        const userId = editingUser?.id_usuari || editingUser?.id;
        if (!userId) return;

        let isMounted = true;
        setLoadingData(true);
        GetUserEquipment(userId).then((eq: any) => {
            if (!isMounted || !eq) return;

            setEquipped({
                id_skin: eq.id_skin ?? null,
                id_arma: eq.id_arma ?? null,
                id_escut: eq.id_escut ?? null,
                id_arc: eq.id_arc ?? null,
                id_flecha: eq.id_flecha ?? null,
                id_magia: eq.id_magia ?? null,
                id_avatar: eq.id_avatar ?? null
            });

            setUnlockedState({
                unlocked_avatars: parseUnlocked(eq.unlocked_avatars),
                unlocked_skins: parseUnlocked(eq.unlocked_skins),
                unlocked_armes: parseUnlocked(eq.unlocked_armes),
                unlocked_escuts: parseUnlocked(eq.unlocked_escuts),
                unlocked_arcs: parseUnlocked(eq.unlocked_arcs),
                unlocked_flechas: parseUnlocked(eq.unlocked_flechas),
                unlocked_magias: parseUnlocked(eq.unlocked_magias)
            });
        }).finally(() => {
            if (isMounted) setLoadingData(false);
        });

        return () => {
            isMounted = false;
        };
    }, [editingUser]);

    /** Helper: guarda un cambio individual con feedback visual */
    const callSave = async (action: () => Promise<boolean>) => {
        if (saving) return;
        setSaving(true);
        const msgKey = 'eq-autosave';
        message.loading({ content: 'Guardando...', key: msgKey, duration: 0 });
        try {
            const ok = await action();
            if (ok) {
                setLastSaved(new Date());
                message.success({ content: 'Guardado ✓', key: msgKey, duration: 1.5 });
                onSaved?.();
            } else {
                message.error({ content: 'Error al guardar el cambio', key: msgKey, duration: 3 });
            }
        } catch {
            message.error({ content: 'Error de conexión al guardar', key: msgKey, duration: 3 });
        } finally {
            setSaving(false);
        }
    };

    const categories: CategoryConfig[] = [
        {
            key: 'skin',
            title: 'Skins',
            icon: <SkinOutlined />,
            color: '#722ed1',
            list: skinsConfig.skins || [],
            equippedField: 'id_skin',
            unlockedKey: 'unlocked_skins',
            hasEquippedState: true
        },
        {
            key: 'arma',
            title: 'Armas',
            icon: <ThunderboltOutlined />,
            color: '#fa541c',
            list: skinsConfig.armes || [],
            equippedField: 'id_arma',
            unlockedKey: 'unlocked_armes',
            hasEquippedState: true
        },
        {
            key: 'escut',
            title: 'Escudos',
            icon: <SafetyCertificateOutlined />,
            color: '#1890ff',
            list: skinsConfig.escuts || [],
            equippedField: 'id_escut',
            unlockedKey: 'unlocked_escuts',
            hasEquippedState: true
        },
        {
            key: 'arc',
            title: 'Arcos',
            icon: <AimOutlined />,
            color: '#13c2c2',
            list: skinsConfig.arcs || [],
            equippedField: 'id_arc',
            unlockedKey: 'unlocked_arcs',
            hasEquippedState: true
        },
        {
            key: 'flecha',
            title: 'Flechas',
            icon: <AimOutlined />,
            color: '#2f54eb',
            list: skinsConfig.flechas || [],
            equippedField: 'id_flecha',
            unlockedKey: 'unlocked_flechas',
            hasEquippedState: true
        },
        {
            key: 'magia',
            title: 'Magias',
            icon: <StarOutlined />,
            color: '#faad14',
            list: skinsConfig.magias || [],
            equippedField: 'id_magia',
            unlockedKey: 'unlocked_magias',
            hasEquippedState: true
        },
        {
            key: 'avatar',
            title: 'Avatares',
            icon: <UserOutlined />,
            color: '#eb2f96',
            list: skinsConfig.avatars || [],
            equippedField: 'id_avatar',
            unlockedKey: 'unlocked_avatars',
            hasEquippedState: false
        }
    ];

    const toggleUnlocked = (unlockedKey: string, itemId: number, equippedField: string, categoryType: string) => {
        if (itemId === 1) return; // ID 1 siempre desbloqueado
        const userId = editingUser?.id_usuari || editingUser?.id;
        const currentList = unlockedState[unlockedKey] || [];
        const isCurrentlyUnlocked = currentList.includes(itemId);

        if (isCurrentlyUnlocked) {
            // Bloquear: quitar de unlocked y si estaba equipado, desequipar
            const newList = currentList.filter((id: number) => id !== itemId);
            let newEquipped = equipped;
            if (equipped[equippedField] === itemId) {
                newEquipped = { ...equipped, [equippedField]: null };
                setEquipped(newEquipped);
            }
            setUnlockedState(prev => ({ ...prev, [unlockedKey]: newList }));
            callSave(() => LockUserItem(userId, categoryType, itemId));
            if (equipped[equippedField] === itemId) {
                // También desequipar en backend
                SetUserEquipped(userId, equippedField, null);
            }
        } else {
            // Desbloquear
            const newList = [...currentList, itemId];
            setUnlockedState(prev => ({ ...prev, [unlockedKey]: newList }));
            callSave(() => UnlockUserItem(userId, categoryType, itemId));
        }
    };

    const toggleEquipped = (equippedField: string, itemId: number, unlockedKey: string, categoryType: string) => {
        const userId = editingUser?.id_usuari || editingUser?.id;
        const currentEquipped = equipped[equippedField];

        if (currentEquipped === itemId) {
            // Desequipar
            setEquipped(prev => ({ ...prev, [equippedField]: null }));
            callSave(() => SetUserEquipped(userId, equippedField, null));
        } else {
            // Equipar (y desbloquear automáticamente si no lo estaba)
            setEquipped(prev => ({ ...prev, [equippedField]: itemId }));
            const currentList = unlockedState[unlockedKey] || [];
            if (!currentList.includes(itemId)) {
                setUnlockedState(prev => ({ ...prev, [unlockedKey]: [...currentList, itemId] }));
                // Unlock en backend primero, luego equip
                callSave(async () => {
                    const unlocked = await UnlockUserItem(userId, categoryType, itemId);
                    if (!unlocked) return false;
                    return SetUserEquipped(userId, equippedField, itemId);
                });
            } else {
                callSave(() => SetUserEquipped(userId, equippedField, itemId));
            }
        }
    };

    // Resumen superior de elementos equipados
    const renderEquippedSummary = () => {
        const equippedBadges = categories
            .filter(c => c.hasEquippedState)
            .map(c => {
                const eqId = equipped[c.equippedField];
                const item = c.list.find(x => x.id === eqId);
                const img = item ? javamonImage(item, c.key) : null;
                return (
                    <Tooltip
                        key={c.key}
                        title={
                            <div>
                                <div style={{ fontWeight: 'bold' }}>{c.title}</div>
                                <div>{item ? item.nom : 'Ninguno equipado'}</div>
                            </div>
                        }
                    >
                        <div
                            onClick={() => setActiveTab(c.key)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '4px 10px',
                                background: item ? '#f0f5ff' : '#fafafa',
                                border: `1.5px solid ${item ? c.color : '#e2e8f0'}`,
                                borderRadius: 8,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                transform: activeTab === c.key ? 'scale(1.05)' : 'none',
                                boxShadow: activeTab === c.key ? `0 2px 8px ${c.color}40` : 'none'
                            }}
                        >
                            <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {img ? (
                                    <img src={img} alt={c.title} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                ) : (
                                    <span style={{ fontSize: 16, opacity: 0.35 }}>{c.icon}</span>
                                )}
                            </div>
                            <div style={{ lineHeight: 1.2 }}>
                                <div style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase', fontWeight: 600 }}>{c.title}</div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: item ? '#1f1f1f' : '#bfbfbf', maxWidth: 90, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {item ? item.nom : '—'}
                                </div>
                            </div>
                        </div>
                    </Tooltip>
                );
            });

        return (
            <div style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                padding: '12px 16px',
                borderRadius: 12,
                border: '1px solid #e2e8f0',
                marginBottom: 16
            }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>🛡️ EQUIPAMIENTO ACTUAL</span>
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>(Haz clic en una ranura para gestionarla)</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {equippedBadges}
                </div>
            </div>
        );
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {editingUser?.profile_avatar ? (
                        <img
                            src={editingUser.profile_avatar}
                            alt="avatar"
                            style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover', border: '2px solid #1890ff' }}
                        />
                    ) : (
                        <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#e6f7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                            👤
                        </div>
                    )}
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                            {editingUser?.nom} {editingUser?.cognoms}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400 }}>
                            {editingUser?.email}
                            {editingUser?.grup && <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>{editingUser.grup}</Tag>}
                        </div>
                    </div>
                </div>
            }
            open={visible}
            onCancel={onClose}
            destroyOnClose
            width={1200}
            style={{ top: 20, maxWidth: '96vw' }}
            styles={{ body: { padding: '16px 20px', maxHeight: '78vh', overflowY: 'auto' } }}
            footer={[
                <div key="footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 12, color: saving ? '#1890ff' : lastSaved ? '#52c41a' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {saving ? (
                            <><SyncOutlined spin /> Guardando cambio...</>
                        ) : lastSaved ? (
                            <><SaveOutlined /> Guardado automáticamente {lastSaved.toLocaleTimeString()}</>
                        ) : (
                            <span style={{ color: '#94a3b8' }}>Los cambios se guardan automáticamente al hacer clic</span>
                        )}
                    </div>
                    <Button onClick={onClose} disabled={saving}>
                        Cerrar
                    </Button>
                </div>
            ]}
        >
            <Spin spinning={loadingData}>
                {renderEquippedSummary()}

                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    items={categories.map(cat => {
                    const unlockedCount = (unlockedState[cat.unlockedKey] || []).length;
                    const totalCount = cat.list.length;

                    return {
                        key: cat.key,
                        label: (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ color: cat.color }}>{cat.icon}</span>
                                <b>{cat.title}</b>
                                <Badge
                                    count={`${unlockedCount}/${totalCount}`}
                                    style={{
                                        backgroundColor: unlockedCount === totalCount ? '#52c41a' : '#f0f0f0',
                                        color: unlockedCount === totalCount ? '#fff' : '#595959',
                                        fontSize: 11,
                                        boxShadow: 'none'
                                    }}
                                />
                            </span>
                        ),
                        children: (
                            <div>
                                {/* Barra de acciones de la categoría */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 14,
                                    padding: '8px 12px',
                                    background: '#f8fafc',
                                    borderRadius: 8,
                                    border: '1px solid #f1f5f9'
                                }}>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>
                                        {cat.hasEquippedState ? (
                                            <>
                                                Usa el botón <b>Equipar</b> para asignarlo como arma/skin activa. Usa el botón <b>Desbloquear</b> para darle acceso.
                                            </>
                                        ) : (
                                            <>
                                                Selecciona los avatares que este alumno puede elegir en su perfil.
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Grilla de tarjetas interactivas */}
                                {cat.list.length === 0 ? (
                                    <Empty description="No hay elementos configurados en esta categoría" />
                                ) : (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                        gap: 12
                                    }}>
                                        {cat.list.map(item => {
                                            const isUnlocked = (unlockedState[cat.unlockedKey] || []).includes(item.id);
                                            const isEquipped = cat.hasEquippedState && equipped[cat.equippedField] === item.id;
                                            const isDefault = item.id === 1;
                                            const img = javamonImage(item, cat.key);

                                            return (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        position: 'relative',
                                                        borderRadius: 12,
                                                        background: isEquipped ? '#f0f5ff' : isUnlocked ? '#ffffff' : '#f8fafc',
                                                        border: isEquipped
                                                            ? `2px solid ${cat.color}`
                                                            : isUnlocked
                                                                ? '1.5px solid #b7eb8f'
                                                                : '1.5px dashed #cbd5e1',
                                                        padding: '12px 10px',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        textAlign: 'center',
                                                        boxShadow: isEquipped
                                                            ? `0 4px 14px ${cat.color}35`
                                                            : isUnlocked
                                                                ? '0 2px 6px rgba(0,0,0,0.05)'
                                                                : 'none',
                                                        opacity: !isUnlocked ? 0.6 : 1,
                                                        transition: 'all 0.2s',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        if (cat.hasEquippedState) {
                                                            toggleEquipped(cat.equippedField, item.id, cat.unlockedKey, cat.key);
                                                        } else {
                                                            toggleUnlocked(cat.unlockedKey, item.id, cat.equippedField, cat.key);
                                                        }
                                                    }}
                                                >
                                                    {/* Badge de Equipado */}
                                                    {isEquipped && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: -8,
                                                            right: -8,
                                                            background: cat.color,
                                                            color: '#fff',
                                                            borderRadius: '50%',
                                                            width: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: 13,
                                                            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                                                            zIndex: 2
                                                        }}>
                                                            <CheckOutlined />
                                                        </div>
                                                    )}

                                                    {/* Imagen del item */}
                                                    <div style={{
                                                        width: 64,
                                                        height: 64,
                                                        marginBottom: 8,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        background: isEquipped ? '#ffffff' : '#f1f5f9',
                                                        borderRadius: 10,
                                                        padding: 6,
                                                        border: '1px solid #e2e8f0'
                                                    }}>
                                                        {img ? (
                                                            <img
                                                                src={img}
                                                                alt={item.nom}
                                                                style={{
                                                                    maxWidth: '100%',
                                                                    maxHeight: '100%',
                                                                    objectFit: 'contain',
                                                                    filter: !isUnlocked ? 'grayscale(90%)' : 'none'
                                                                }}
                                                            />
                                                        ) : (
                                                            <span style={{ fontSize: 24, opacity: 0.4 }}>{cat.icon}</span>
                                                        )}
                                                    </div>

                                                    {/* Nombre e ident */}
                                                    <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', marginBottom: 2, lineHeight: 1.2 }}>
                                                        {item.nom}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
                                                        {item.ident || (isDefault ? 'Por defecto' : `#${item.id}`)}
                                                    </div>

                                                    {/* Atributos / Stats si tiene */}
                                                    <div style={{ minHeight: 20, marginBottom: 8 }}>
                                                        {item.type && <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>{item.type}</Tag>}
                                                        {item.dany !== undefined && item.dany !== null && <Tag color="red" style={{ fontSize: 10, margin: 0 }}>⚔️ {item.dany}</Tag>}
                                                        {item.defensa !== undefined && item.defensa !== null && <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>🛡️ {item.defensa}</Tag>}
                                                        {item.velocitat !== undefined && item.velocitat !== null && <Tag color="green" style={{ fontSize: 10, margin: 0 }}>⚡ {item.velocitat}</Tag>}
                                                    </div>

                                                    {/* Botones de acción individuales */}
                                                    <div
                                                        style={{ width: '100%', display: 'flex', gap: 4, marginTop: 'auto' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {cat.hasEquippedState && (
                                                            <Button
                                                                size="small"
                                                                type={isEquipped ? 'primary' : 'default'}
                                                                style={{
                                                                    flex: 1,
                                                                    fontSize: 11,
                                                                    backgroundColor: isEquipped ? cat.color : undefined,
                                                                    borderColor: isEquipped ? cat.color : undefined
                                                                }}
                                                                onClick={() => toggleEquipped(cat.equippedField, item.id, cat.unlockedKey, cat.key)}
                                                            >
                                                                {isEquipped ? 'Equipado' : 'Equipar'}
                                                            </Button>
                                                        )}

                                                        <Button
                                                            size="small"
                                                            type={isUnlocked ? 'dashed' : 'default'}
                                                            danger={isUnlocked && !isDefault}
                                                            disabled={isDefault}
                                                            style={{
                                                                flex: cat.hasEquippedState ? '0 0 32px' : 1,
                                                                fontSize: 11,
                                                                padding: 0,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                            title={isDefault ? 'Por defecto (siempre desbloqueado)' : isUnlocked ? 'Bloquear elemento' : 'Desbloquear elemento'}
                                                            onClick={() => toggleUnlocked(cat.unlockedKey, item.id, cat.equippedField, cat.key)}
                                                        >
                                                            {isDefault ? (
                                                                <Tooltip title="Por defecto">
                                                                    <CheckCircleFilled style={{ color: '#52c41a' }} />
                                                                </Tooltip>
                                                            ) : isUnlocked ? (
                                                                cat.hasEquippedState ? <UnlockOutlined style={{ color: '#52c41a' }} /> : 'Bloquear'
                                                            ) : (
                                                                cat.hasEquippedState ? <LockOutlined style={{ color: '#94a3b8' }} /> : 'Desbloquear'
                                                            )}
                                                        </Button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )
                    };
                })}
            />
            </Spin>
        </Modal>
    );
};

export default UserEquipmentEditModal;
